<template>
  <div class="ui form">
    <div class="field">
      <div class="global-h4">Lodging Details</div>
      <p v-if="collabRemainingCounts.mustCoverLodging && collabMinHotelNights === 1" class="cms">
        Creators typically stay <strong>1-2 nights</strong> in a destination.<br />
        Choose how many nights you can arrange.
      </p>
      <p v-else-if="collabRemainingCounts.mustCoverLodging" class="cms">Creators typically stay <strong>2 nights</strong> in a destination.</p>
      <p v-else-if="canChooseNoCoverage && !canChoosePaidAddOn" class="cms">
        Creators typically stay <strong>2 nights</strong> in a destination.<br />
        You can arrange lodging yourself, or choose no lodging coverage.
      </p>
      <p v-else class="cms">
        You have purchased the lodging add-on for a subset of your creator visits.<br />
        You can choose to use the add-on for this visit, or arrange lodging yourself.
      </p>
      <!--Note: Also in VisitCheckList-->
      <div
        v-if="showLodgingSelect"
        :class="{
          'form-error-highlight': true,
          'form-error-highlight--bg-visible': errors.includes('Lodging Details'),
        }"
      >
        <!--NOTE! Any changes here update VisitChecklist also!-->
        <!-- Coverage-driven choice: paid add-on and NoCoverage both use 0 nights; arrange1 vs arrange2 both use CustomerChoseToArrange -->
        <select ref="hotelCovered" v-model="lodgingChoiceKey" @change="onLodgingChoiceChanged" style="width: 70vw; max-width: 350px">
          <option disabled :value="null">Please select one</option>
          <option v-if="canChoosePaidAddOn" value="paidAddOn">{{ paidAddOnOptionText }}</option>
          <option v-if="canChooseNoCoverage" value="noCoverage">No Lodging Coverage</option>
          <option v-if="canChooseArrange1Night" value="arrange1">Can arrange a 1-night stay</option>
          <option value="arrange2">Can arrange a 2-night stay</option>
        </select>
      </div>
    </div>
    <div v-if="lodgingChoiceKey === 'paidAddOn'" class="cms" style="margin-top: 0.8em">
      Shrpa will pass through the ${{ hotelCoverageFromShrpa }} Lodging Add-on to the creator.
    </div>
    <NoteWithIcon v-if="collabInput.hotelCoverageUsed === 'NoCoverage'" size="tiny" color="yellow" style="margin-top: 0.8em">
      <template #icon><IconEmbedded name="info-simple_4" /></template>
      <template #text>
        <span>No lodging coverage will reduce the number of creators that apply for this visit.</span>
      </template>
    </NoteWithIcon>
    <template v-if="collabInput.hotelNightsCovered && collabInput.hotelNightsCovered > 0">
      <div class="field" style="margin-top: 0.8em">
        <label class="section-title">Lodging Name</label>
        <p class="cms">Name of the lodging you will arrange? (Can be answered later)</p>
        <input type="text" v-model="collabInput.hotelName" />
      </div>
      <div class="field">
        <label class="section-title">Estimated Lodging Value</label>
        <p class="cms">
          We show this to the creator to convey the total value they're receiving.<br />
          If you aren't sure, just put in an estimate.
        </p>
        <div class="estimated-lodging-value-row">
          <i class="dollar sign icon estimated-lodging-value-row__dollar-icon"></i>
          <div
            :class="{
              'form-error-highlight': true,
              'form-error-highlight--bg-visible': errors.includes('Estimated Lodging Value'),
            }"
            style="display: inline-flex"
          >
            <input type="number" v-model.number="collabInput.hotelEstimatedValue" style="width: 80px" />
          </div>
          <span class="estimated-lodging-value-row__label">total value</span>
          <InfoIconWithTooltip :width="280" class="estimated-lodging-value-row__tooltip">Total value of the stay across nights (not per-night). This will be shown to creators. </InfoIconWithTooltip>
        </div>
      </div>
    </template>
    <div class="field" style="margin-top: 2em">
      <LinkWithIcon class="onboarding-slide__previous-step-link" tag="a" :href="globalGetLink('CollabHotelCompTemplate')" target="_blank" @click="$emit('goToPrevStep')">
        <template #icon><IconEmbedded name="bulb_2" :size="21" /></template>
        <span>Ideas for reaching out to lodging partners</span>
      </LinkWithIcon>
    </div>
  </div>
</template>

<script lang="ts">
import CollabValueCalculator from "@logic/CollabValueCalculator";
import { defineComponent } from "vue";

// Types
import { CollabInput, CustomerCollabRemainingCounts, DefaultLodgingNightsRequired } from "@contracts/collab";

// Components
import CollabCompTags from "@views/PaidCollab/CollabCompTags.vue";
import ItineraryTile from "@components/ItineraryTile.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import SpoilerWithIcon from "@components/SpoilerWithIcon.vue";
import { inject } from "vue";
import logger from "@helpers/Logger";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import InfoIconWithTooltip from "@components/ui/InfoIconWithTooltip.vue";

export default defineComponent({
  name: "StepLodging",

  components: {
    IconEmbedded,
    InfoIconWithTooltip,
    CollabCompTags,
    ItineraryTile,
    LinkWithIcon,
    NoteWithIcon,
    SpoilerWithIcon,
  },

  props: {
    collabInput: { type: Object as () => CollabInput | null, required: true },
    collabRemainingCounts: { type: Object as () => CustomerCollabRemainingCounts | null, required: false, default: null },
    errors: { type: Array<string>, required: true },
  },

  emits: ["goToPrevStep"],

  data() {
    return {
      globalGetLink: inject("globalGetLink") as any,
      DefaultLodgingNightsRequired,
    };
  },

  computed: {
    hotelCoverageFromShrpa(): number {
      return this.collabRemainingCounts?.hotelCreatorComp || CollabValueCalculator.HotelCoverageFromShrpa;
    },
    collabMinHotelNights(): number {
      return this.collabRemainingCounts?.collabMinHotelNights ?? DefaultLodgingNightsRequired;
    },
    canChoosePaidAddOn(): boolean {
      return (this.collabRemainingCounts?.collabHotelsPaidRemaining ?? 0) > 0 || this.collabInput.hotelCoverageUsed === "CustomerChosePaidAddOn" || this.collabInput.hotelCoverageUsed === "PaidAddOn";
    },
    canChooseNoCoverage(): boolean {
      return this.collabMinHotelNights === 0 || this.collabInput.hotelCoverageUsed === "NoCoverage";
    },
    canChooseArrange1Night(): boolean {
      return this.collabMinHotelNights <= 1 || this.collabInput.hotelNightsCovered === 1;
    },
    /** Only force-hide when must arrange and min is the full default (2). Min 0/1 always shows a choice. */
    showLodgingSelect(): boolean {
      return (
        this.collabInput.hotelCoverageUsed === "Grandfathered" || this.canChoosePaidAddOn || this.collabMinHotelNights < DefaultLodgingNightsRequired || !this.collabRemainingCounts?.mustCoverLodging
      );
    },
    paidAddOnOptionText(): string {
      return `Use Paid Lodging Add-on (${this.collabRemainingCounts.collabHotelsPaidRemaining} remaining)`;
    },
    lodgingChoiceKey: {
      get(): string | null {
        // NOTE! Mirrored in VisitCheckList!
        const used = this.collabInput.hotelCoverageUsed;
        const nights = this.collabInput.hotelNightsCovered;
        if (used === "CustomerChosePaidAddOn" || used === "PaidAddOn") return "paidAddOn";
        // Destination-arranged nights (1 or 2) are never NoCoverage
        if (nights === 1) return "arrange1";
        if (used === "CustomerChoseToArrange" || (nights ?? 0) > 0) return "arrange2";
        if (used === "NoCoverage") return "noCoverage";
        return null;
      },
      set(value: string | null) {
        if (value) this.applyLodgingChoice(value);
      },
    },
  },

  mounted() {
    this.$nextTick(() => {
      (this.$refs.hotelCovered as HTMLInputElement)?.focus();
    });
    // Repair inconsistent state: arranged nights must not stay on NoCoverage
    if ((this.collabInput.hotelNightsCovered ?? 0) > 0 && this.collabInput.hotelCoverageUsed === "NoCoverage") {
      this.collabInput.hotelCoverageUsed = "CustomerChoseToArrange";
    }
    // Only force 2 nights when must cover and min is the full default (no 1-night choice)
    if (this.collabRemainingCounts?.mustCoverLodging && this.collabMinHotelNights >= DefaultLodgingNightsRequired) {
      this.collabInput.hotelNightsCovered = DefaultLodgingNightsRequired;
      if (!this.collabInput.hotelCoverageUsed || this.collabInput.hotelCoverageUsed === "Unset") {
        this.collabInput.hotelCoverageUsed = "CustomerChoseToArrange";
      }
    }
  },
  methods: {
    applyLodgingChoice(choice: string) {
      // NOTE! Mirrored in VisitChecklist!
      // arrange1/arrange2 = destination arranges lodging (CustomerChoseToArrange), never NoCoverage
      if (choice === "paidAddOn") {
        this.collabInput.hotelCoverageUsed = "CustomerChosePaidAddOn";
        this.collabInput.hotelNightsCovered = 0;
        this.collabInput.hotelEstimatedValue = null;
      } else if (choice === "noCoverage") {
        this.collabInput.hotelCoverageUsed = "NoCoverage";
        this.collabInput.hotelNightsCovered = 0;
        this.collabInput.hotelEstimatedValue = null;
      } else if (choice === "arrange1") {
        this.collabInput.hotelCoverageUsed = "CustomerChoseToArrange";
        this.collabInput.hotelNightsCovered = 1;
      } else {
        this.collabInput.hotelCoverageUsed = "CustomerChoseToArrange";
        this.collabInput.hotelNightsCovered = DefaultLodgingNightsRequired;
      }
      logger.info("StepLodging onLodgingChoiceChanged", this.collabInput.hotelCoverageUsed, this.collabInput.hotelNightsCovered);
    },
    onLodgingChoiceChanged() {
      // v-model setter already applied the choice
    },
  },
});
</script>

<style scoped lang="scss">
@import "./form-error-highlight.scss";

.estimated-lodging-value-row {
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;

  &__dollar-icon {
    margin: 0;
    padding: 0;
    line-height: 1;
  }

  &__label {
    margin-left: 5px;
  }

  &__tooltip {
    margin-left: 8px;
  }
}
</style>
